import { Copy, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { UnitPriceInput } from "./UnitPriceInput";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { formatVND, formatVatVND } from "./e-invoice-utils";
import type { EInvoicePayloadLineItem } from "@/types/e-invoice-request";

export interface LineItemsEditorProps {
  items: EInvoicePayloadLineItem[];
  saleItems?: {
    description?: string | null;
    productName?: string | null;
    quantity: number;
    unitPrice: number;
  }[];
  onChange: (items: EInvoicePayloadLineItem[]) => void;
}

export function LineItemsEditor({
  items,
  saleItems,
  onChange,
}: LineItemsEditorProps) {
  function updateField<K extends keyof EInvoicePayloadLineItem>(
    index: number,
    field: K,
    value: EInvoicePayloadLineItem[K],
  ) {
    const next = items.map((item, i) =>
      i === index ? { ...item, [field]: value } : item,
    );
    onChange(next);
  }

  function handleDuplicate(index: number) {
    // Deep-clone the row so editing the copy doesn't mutate the original.
    const clone = JSON.parse(
      JSON.stringify(items[index]),
    ) as EInvoicePayloadLineItem;
    const next = [...items];
    next.splice(index + 1, 0, clone);
    onChange(next);
  }

  function handleRemove(index: number) {
    onChange(items.filter((_, i) => i !== index));
  }

  return (
    <div className="space-y-2">
      <div className="rounded-md border overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Product</TableHead>
              <TableHead className="w-28">Unit Price</TableHead>
              <TableHead className="w-20">VAT %</TableHead>
              <TableHead className="text-right">VAT Amount</TableHead>
              <TableHead className="text-right">Total</TableHead>
              <TableHead className="w-20" />
            </TableRow>
          </TableHeader>
          <TableBody>
            {items.map((item, idx) => {
              const saleItem = saleItems?.find(
                (sale) =>
                  (sale.description || sale.productName || "Product") ===
                  item.name,
              );
              return (
                <TableRow key={idx}>
                  <TableCell>
                    <Input
                      value={item.name}
                      readOnly
                      title={item.name}
                      className="h-8 text-sm font-semibold min-w-[140px] bg-gray-200 text-gray-800 dark:bg-gray-700 dark:text-gray-100 cursor-default"
                      placeholder="Product name"
                    />
                    {saleItem && (
                      <div className="mt-1 text-xs text-muted-foreground">
                        SL:{" "}
                        {new Intl.NumberFormat("vi-VN").format(
                          saleItem.quantity,
                        )}
                        {" × "}
                        <span className="text-red-600 dark:text-red-400">
                          {formatVND(saleItem.unitPrice)}
                        </span>
                      </div>
                    )}
                  </TableCell>
                  <TableCell>
                    <UnitPriceInput
                      value={item.unitPrice}
                      label={`Unit Price for ${item.name}`}
                      onChange={(value) => updateField(idx, "unitPrice", value)}
                    />
                  </TableCell>
                  <TableCell>
                    <Input
                      type="number"
                      min={-2}
                      value={item.taxRate}
                      onChange={(e) =>
                        updateField(idx, "taxRate", Number(e.target.value))
                      }
                      className="h-8 text-sm w-full min-w-[60px]"
                    />
                  </TableCell>
                  <TableCell className="text-right text-sm font-medium align-middle whitespace-nowrap">
                    {formatVatVND(item.vatAmount)}
                  </TableCell>
                  <TableCell className="text-right text-sm font-medium align-middle whitespace-nowrap">
                    {formatVND(item.totalAmount)}
                  </TableCell>
                  <TableCell>
                    <div className="flex gap-1">
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="h-8 w-8 p-0"
                        title="Duplicate line"
                        onClick={() => handleDuplicate(idx)}
                      >
                        <Copy className="h-3.5 w-3.5" />
                      </Button>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="h-8 w-8 p-0 text-destructive-ink hover:text-destructive-ink"
                        title="Remove line"
                        onClick={() => handleRemove(idx)}
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              );
            })}
            {items.length === 0 && (
              <TableRow>
                <TableCell
                  colSpan={6}
                  className="text-center text-sm text-muted-foreground italic py-4"
                >
                  No line items.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
